import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState
} from 'react';

import {
    IconArrowLeft,
    IconArrowOutwards,
    IconArrowRight,
    IconClose
} from '$partials/icon/index.js';

import {
    Media,
    SimpleImage
} from '$partials/media/index.js';

import { useDialog } from '$hooks/dialog.js';

/* ========================================================================= */
/* Context */
/* ========================================================================= */

/**
 * @type {React.Context<import('$types/components/partials/base.d.ts').LightboxContextType>}
 */
const Context = createContext(null);

Context.displayName = 'LightboxContext';

/**
 * @type {import('$types/components/partials/base.d.ts').UseLightbox}
 */
const useLightbox = () => useContext(Context);

/* ========================================================================= */
/* Helper(s) */
/* ========================================================================= */

/**
 * The still that stands for an item in the thumbnail strip — an image's own
 * source, or the poster a video asset carries.
 */
const posterFor = (media) => media.image?.src || media.video?.poster || media.videoEmbed?.poster || media.videoEmbed?.embed?.thumbnailUrl;

/**
 * The width and height of the still a thumbnail is cropped from, so a
 * thumbnail can take the same orientation as the media it stands for.
 */
const dimensionsFor = (media) => media.image || media.video || media.videoEmbed?.embed;

/**
 * Whether a thumbnail should be cropped tall rather than wide — an item
 * carrying no dimensions falls back to the landscape box every other still
 * in the strip uses.
 */
const isPortrait = (media) => {
    const dimensions = dimensionsFor(media);

    return !!dimensions && (dimensions.height > dimensions.width);
};

/**
 * What an item is called where only one line is going to be read — a control's
 * accessible name, or a thumbnail's.
 */
const nameFor = (item, index) => item.heading || item.text || `Item ${index + 1}`;

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/base.d.ts').LightboxProvider}
 */
const LightboxProvider = ({ children }) => {
    // contexts
    const {
        dialogRef,
        handleClose,
        handleOpen,
        scrollRef
    } = useDialog({ exitDelay: 0 });

    // references
    const closeRef = useRef(handleClose);
    const openRef = useRef(handleOpen);

    // state
    const [activeIndex, setActiveIndex] = useState(undefined);
    const [items, setItems] = useState([]);

    // methods
    const close = () => {
        setActiveIndex(undefined);
        closeRef.current();
    };

    const open = (id = '') => {
        const index = items.findIndex((item) => (item.id === id));
        setActiveIndex(index);
        openRef.current();
    };

    const select = (index = 0) => {
        // const index = items.findIndex((item) => (item.id === id));

        if (
            (index > -1)
            && (index <= (items.length - 1))
        ) {
            setActiveIndex(index);
        }
    };

    const next = () => {
        select(activeIndex + 1);
    };

    const previous = () => {
        select(activeIndex - 1);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'ArrowLeft') {
            previous();
        }

        if (event.key === 'ArrowRight') {
            next();
        }
    };

    // hooks
    const register = useCallback((item) => {
        setItems((current) => {
            const index = current.findIndex((registered) => (registered.id === item.id));

            if (index === -1) {
                return [...current, item];
            }

            const next = [...current];
            next[index] = item;
            return next;
        });

        return () => {
            setItems(
                (current) => current.filter(
                    (registered) => (registered.id !== item.id)
                )
            );
        };
    }, []);

    const context = useMemo(() => ({
        open,
        register
    }), [
        open,
        register
    ]);

    // misc
    const item = items[activeIndex];
    const hasPrevious = (activeIndex > 0);
    const hasNext = ((activeIndex > -1) && (activeIndex < items.length - 1));

    // render
    return (
        <Context.Provider value={context}>
            {children}

            <dialog
                className='dialog'
                onClick={(event) => (event?.target === dialogRef?.current) && close()}
                onClose={close}
                onKeyDown={handleKeyDown}
                ref={dialogRef}>
                {item && (
                    <div
                        className='relative h-full w-full flex flex-col bg-neutral-00 select-none'
                        ref={scrollRef}>
                        {/* Previous and next */}
                        <div className='absolute right-16 bottom-110 z-20 flex gap-x-8'>
                            <button
                                aria-disabled={!hasPrevious}
                                aria-label='Previous item'
                                className='button-round'
                                onClick={previous}
                                type='button'>
                                <IconArrowLeft />
                            </button>

                            <button
                                aria-disabled={!hasNext}
                                aria-label='Next item'
                                className='button-round'
                                onClick={next}
                                type='button'>
                                <IconArrowRight />
                            </button>
                        </div>

                        {/* Close */}
                        <button
                            aria-label='Close'
                            className='button-round absolute right-16 sm:right-24 top-16 sm:top-24 z-20'
                            onClick={close}
                            type='button'>
                            <IconClose />
                        </button>

                        {/*  Mobile only navigation */}
                        <div
                            className='sm:hidden absolute top-0 h-[calc(100dvh-108px)] left-0 w-48 z-10'
                            onClick={previous}/>
                        <div
                            className='sm:hidden absolute top-0 h-[calc(100dvh-108px)] right-0 w-48 z-10'
                            onClick={next}/>

                        <div className='flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden'>
                            <div className='h-full w-full flex items-center justify-center relative'>
                                <Media
                                    image={item.media.image ? {
                                        ...item.media.image,
                                        className: 'h-full w-full object-center object-contain'
                                    } : undefined}
                                    imageComponent={SimpleImage}
                                    key={item.id}
                                    video={item.media.video ? {
                                        autoPlay: true,
                                        controls: false,
                                        loop: true,
                                        muted: true,
                                        ...item.media.video
                                    } : undefined}
                                    videoEmbed={item.media.videoEmbed ? {
                                        ...item.media.videoEmbed,
                                    } : undefined} />

                                {!item.media.videoEmbed && (
                                    <div className='absolute inset-0 h-full w-full' />
                                )}
                            </div>
                        </div>

                        {/* Caption and thumbnails */}
                        <div className='shrink-0 px-8 pb-10 pt-10 flex flex-col items-start gap-y-8 select-auto'>
                            {(item.heading || item.text) && (
                                <div>
                                    {item.heading && (
                                        <p className='type-style-body-bold'>{item.heading}</p>
                                    )}

                                    {item.text && (
                                        <p className='type-style-body'>{item.text}</p>
                                    )}
                                </div>
                            )}

                            {items.length > 1 && (
                                <ul className='flex flex-nowrap sm:flex-wrap w-full min-w-0 overflow-x-auto sm:overflow-visible gap-8'>
                                    {items.map((thumbnail, thumbnailIndex) => (
                                        <li
                                            className='shrink-0'
                                            key={thumbnail.id}>
                                            <button
                                                aria-current={thumbnailIndex === activeIndex}
                                                aria-label={nameFor(thumbnail, thumbnailIndex)}
                                                className={`block h-38 ${isPortrait(thumbnail.media) ? 'w-36' : 'w-68'} overflow-hidden cursor-pointer pointer-events-auto${thumbnailIndex === activeIndex ? ' opacity-100' : ' opacity-50'}`}
                                                onClick={() => select(thumbnailIndex)}
                                                type='button'>
                                                <img
                                                    alt=''
                                                    className='h-full w-full object-cover object-center pointer-events-none select-none'
                                                    src={posterFor(thumbnail.media)}/>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </dialog>
        </Context.Provider>
    );
};

/**
 * @type {import('$types/components/partials/base.d.ts').LightboxItem}
 */
const LightboxItem = ({
    children = null,
    heading = null,
    media = null,
    text = null,
    wrapperClassName
}) => {
    // contexts
    const {
        open,
        register
    } = useLightbox();

    // hooks
    const id = useId();

    useEffect(() => {
        return register({
            heading,
            id,
            media,
            text
        });
    }, [
        heading,
        id,
        media,
        register,
        text
    ]);

    // misc
    const caption = heading || text;

    // render
    return (
        <div className={`${wrapperClassName ? wrapperClassName : ''} group relative`}>
            {children}

            {media && (
                <button
                    aria-label={caption ? `View ${caption} larger` : 'View media larger'}
                    className='absolute inset-0 flex items-end justify-end p-8 sm:p-16 cursor-pointer'
                    onClick={() => open(id)}
                    type='button'>
                    <span className='button-round bg-neutral-950 text-neutral-00'>
                        <IconArrowOutwards />
                    </span>
                </button>
            )}
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export {
    Context as LightboxContext,
    LightboxItem,
    LightboxProvider,
    useLightbox
};
